import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import styles from './Registry.module.css'; // Import CSS module
import { Mail, Lock, User, Phone, Home, Plus, Shield, ArrowLeft, Eye, EyeOff } from 'lucide-react';
import { registerUser } from '../../Fetch/fetchUser';
import { fetchCity, fetchStreets, searchStreets } from '../../Fetch/fetchAddress';
import EmailVerification from './EmailVerification'; // Import the verification component
import _ from 'lodash'; // Make sure lodash is installed

export default function Registry({ 
    setIsLoggedIn, 
    setUserData, 
    setCurrentPage, 
    showBackButton = false, 
    onBack 
}) {
    const location = useLocation();
    const [formData, setFormData] = useState({
        fullName: '',
        email: '',
        password: '',
        confirmPassword: '',
        phone: '',
        city: '',
        createDate: new Date().toISOString().split('T')[0], // Automatically set to today
        address: '',
        addressNumber: '',
        numApartments: '',
        numFloors: '',
        apartmentNumber: '',
        isVaad: '',
    });

    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    
    // Password validation states
    const [passwordErrors, setPasswordErrors] = useState([]);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    // Email verification states
    const [verificationStep, setVerificationStep] = useState('form'); // 'form', 'verification', 'success'
    const [pendingEmail, setPendingEmail] = useState('');

    // City autocomplete state
    const [citySuggestions, setCitySuggestions] = useState([]);
    const [showCitySuggestions, setShowCitySuggestions] = useState(false);
    const [newCityMode, setNewCityMode] = useState(false);
    const citySuggestionsRef = useRef(null);
    const [isCitySearching, setIsCitySearching] = useState(false);

    // Address autocomplete state
    const [addressSuggestions, setAddressSuggestions] = useState([]);
    const [showAddressSuggestions, setShowAddressSuggestions] = useState(false);
    const [newAddressMode, setNewAddressMode] = useState(false);
    const [isAddressSearching, setIsAddressSearching] = useState(false);
    const addressSuggestionsRef = useRef(null);

    // Store data
    const [israeliCities, setIsraeliCities] = useState([]);
    const [popularStreets, setPopularStreets] = useState([]);

    // Check if we should show verification immediately (from login navigation)
    useEffect(() => {
        if (location.state?.showVerification && location.state?.email) {
            setVerificationStep('verification');
            setPendingEmail(location.state.email);
        }
    }, [location.state]);

    // Password validation function
    const validatePassword = (password) => {
        const errors = [];
        
        if (password.length < 8) {
            errors.push('הסיסמה חייבת להכיל לפחות 8 תווים');
        }
        
        if (!/[a-z]/.test(password)) {
            errors.push('הסיסמה חייבת להכיל לפחות אות קטנה באנגלית');
        }
        
        if (!/[0-9]/.test(password)) {
            errors.push('הסיסמה חייבת להכיל לפחות ספרה אחת');
        }
        
        return errors;
    };

    // Function to format phone number with dash
    const formatPhoneNumber = (value) => {
        // Remove all non-digits
        const phoneNumber = value.replace(/\D/g, '');
        
        // Apply formatting based on length
        if (phoneNumber.length <= 3) {
            return phoneNumber;
        } else if (phoneNumber.length <= 10) {
            return `${phoneNumber.slice(0, 3)}-${phoneNumber.slice(3)}`;
        } else {
            // Limit to 10 digits maximum
            return `${phoneNumber.slice(0, 3)}-${phoneNumber.slice(3, 10)}`;
        }
    };

    // Function to get clean phone number (digits only)
    const getCleanPhoneNumber = (formattedPhone) => {
        return formattedPhone.replace(/\D/g, '');
    };

    // Simple error handling function
    const logError = (message, error) => {
    };

    // Debounced search function for streets
    const debouncedSearchStreets = useCallback(
        _.debounce(async (searchQuery) => {
            if (searchQuery?.length < 2) {
                setAddressSuggestions([]);
                setShowAddressSuggestions(false);
                setIsAddressSearching(false);
                return;
            }

            setIsAddressSearching(true);
            try {
                const results = await searchStreets(searchQuery);
                setAddressSuggestions(Array.isArray(results) ? results : []);
                setShowAddressSuggestions(true);
            } catch (error) {
                // Fallback to local filtering if API fails
                const filtered = popularStreets.filter(street =>
                    typeof street === 'string' &&
                    street.toLowerCase().includes(searchQuery.toLowerCase())
                );
                setAddressSuggestions(filtered);
                setShowAddressSuggestions(filtered?.length > 0);
            } finally {
                setIsAddressSearching(false);
            }
        }, 300),
        [popularStreets]
    );

    // Load cities and streets on component mount
    useEffect(() => {
        const loadCitiesAndStreets = async () => {
            try {
                // Fetch cities
                const citiesResponse = await fetchCity();

                if (Array.isArray(citiesResponse)) {
                    const cityNames = [];
                    citiesResponse.forEach(city => {
                        if (typeof city === 'string') {
                            cityNames.push(city);
                        } else if (city && typeof city === 'object') {
                            const name = city.name || city.cityName;
                            if (name) cityNames.push(name);
                        }
                    });
                    setIsraeliCities(cityNames);
                } else {
                    setIsraeliCities(['תל אביב', 'ירושלים', 'חיפה', 'באר שבע', 'ראשון לציון']);
                }

                // Fetch streets
                const streetsResponse = await fetchStreets();

                if (Array.isArray(streetsResponse)) {
                    const streetNames = [];
                    streetsResponse.forEach(street => {
                        if (typeof street === 'string') {
                            streetNames.push(street);
                        } else if (street && typeof street === 'object') {
                            const name = street.name || street.streetName;
                            if (name) streetNames.push(name);
                        }
                    });
                    setPopularStreets(streetNames);
                } else {
                    setPopularStreets(['הרצל', 'ביאליק', 'ויצמן', 'אלנבי', 'בן גוריון']);
                }
            } catch (error) {
                logError('Error loading initial data:', error);
                setIsraeliCities(['תל אביב', 'ירושלים', 'חיפה', 'באר שבע', 'ראשון לציון']);
                setPopularStreets(['הרצל', 'ביאליק', 'ויצמן', 'אלנבי', 'בן גוריון']);
            }
        };

        loadCitiesAndStreets();
    }, []);

    // Handle click outside to close dropdowns
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (citySuggestionsRef.current && !citySuggestionsRef.current.contains(event.target)) {
                setShowCitySuggestions(false);
            }
            if (addressSuggestionsRef.current && !addressSuggestionsRef.current.contains(event.target)) {
                setShowAddressSuggestions(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;

        // Special handling for phone field with formatting
        if (name === 'phone') {
            const formattedPhone = formatPhoneNumber(value);
            setFormData(prevState => ({
                ...prevState,
                [name]: formattedPhone
            }));
        } else {
            setFormData(prevState => ({
                ...prevState,
                [name]: value
            }));
        }

        // Password validation
        if (name === 'password') {
            const errors = validatePassword(value);
            setPasswordErrors(errors);
        }

        // City autocomplete
        if (name === 'city' && !newCityMode) {
            if (value.trim()) {
                const filtered = israeliCities.filter(city =>
                    typeof city === 'string' && city.toLowerCase().includes(value.toLowerCase())
                );
                setCitySuggestions(filtered);
                setShowCitySuggestions(filtered?.length > 0);
            } else {
                setCitySuggestions([]);
                setShowCitySuggestions(false);
            }
        }

        // Address autocomplete
        if (name === 'address' && !newAddressMode) {
            if (value.trim()) {
                debouncedSearchStreets(value.trim());
            } else {
                setAddressSuggestions([]);
                setShowAddressSuggestions(false);
            }
        }
    };

    const handleSuggestionClick = (name, value) => {
        setFormData(prevState => ({
            ...prevState,
            [name]: value
        }));

        if (name === 'city') {
            setShowCitySuggestions(false);
        } else if (name === 'address') {
            setShowAddressSuggestions(false);
        }
    };

    const handleAddNew = (type) => {
        if (type === 'city') {
            setNewCityMode(true);
            setShowCitySuggestions(false);
        } else if (type === 'address') {
            setNewAddressMode(true);
            setShowAddressSuggestions(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setError('');

        // Validate passwords match
        if (formData.password !== formData.confirmPassword) {
            setError('הסיסמאות אינן תואמות');
            setIsLoading(false);
            return;
        }

        // Validate password strength
        const passwordValidationErrors = validatePassword(formData.password);
        if (passwordValidationErrors.length > 0) {
            setError('הסיסמה לא עומדת בדרישות הבטיחות');
            setPasswordErrors(passwordValidationErrors);
            setIsLoading(false);
            return;
        }

        // Validate form fields (removed createDate from validation since it's auto-set)
        if (!formData.fullName || !formData.email || !formData.password || !formData.phone || !formData.city || !formData.address || !formData.addressNumber) {
            setError('אנא מלא את כל השדות הנדרשים');
            setIsLoading(false);
            return;
        }

        // Validate phone number
        const cleanPhone = getCleanPhoneNumber(formData.phone);
        if (cleanPhone.length < 9 || cleanPhone.length > 10) {
            setError('מספר טלפון לא תקין (9-10 ספרות)');
            setIsLoading(false);
            return;
        }

        try {
            // Prepare form data with clean phone number for backend
            const submissionData = {
                ...formData,
                phone: cleanPhone // Send clean phone number to backend
            };

            // Call registration API
            const result = await registerUser(submissionData);

            // Store pending verification info
            setPendingEmail(formData.email);
            localStorage.setItem('pendingVerification', JSON.stringify({
                email: formData.email,
                timestamp: Date.now()
            }));

            // Move to verification step
            setVerificationStep('verification');

        } catch (error) {
            setError(error.message || 'שגיאה בתהליך ההרשמה. אנא נסה שוב.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleVerificationComplete = (result) => {
        // Clear pending verification
        localStorage.removeItem('pendingVerification');

        // Set success message and redirect to login
        localStorage.setItem('registrationSuccess', 'ההרשמה הושלמה בהצלחה! אנא התחבר למערכת.');
        setCurrentPage('login');
    };

    const handleBackToForm = () => {
        setVerificationStep('form');
        setPendingEmail('');
        localStorage.removeItem('pendingVerification');
    };

    const handleBackClick = () => {
        if (onBack) {
            onBack();
        } else if (setCurrentPage) {
            setCurrentPage('main');
        }
    };

    const handleLoginClick = (e) => {
        e.preventDefault();
        setCurrentPage('login');
    };

    // Show verification component if in verification step
    if (verificationStep === 'verification') {
        return (
            <div>
                {/* Back Button for verification step too */}
                {showBackButton && (
                    <div className={styles.backButtonContainer}>
                        <button
                            onClick={handleBackClick}
                            className={styles.backToHomeButton}
                            type="button"
                        >
                            <ArrowLeft size={20} />
                            חזרה לעמוד הראשי
                        </button>
                    </div>
                )}
                <EmailVerification
                    email={pendingEmail}
                    onVerificationComplete={handleVerificationComplete}
                    onBack={handleBackToForm}
                    setCurrentPage={setCurrentPage}
                />
            </div>
        );
    }

    // Main registration form
    return (
        <div className={styles.registryContainer} dir="rtl">
            {/* Back Button */}
            {showBackButton && (
                <div className={styles.backButtonContainer}>
                    <button
                        onClick={handleBackClick}
                        className={styles.backToHomeButton}
                        type="button"
                    >
                        <ArrowLeft size={20} />
                        חזרה לעמוד הראשי
                    </button>
                </div>
            )}

            <div className={styles.registryCardWrapper}>
                <div className={styles.registryCard}>

                    {error && (
                        <div className={styles.errorMessage}>
                            {error}
                        </div>
                    )}

                    <div className={styles.formContainer}>
                        <div className={styles.header}>
                            <div className={styles.logoContainer}>
                                <User className={styles.logoIcon} />
                            </div>
                            <h1 className={styles.appTitle}>הרשמה למערכת ניהול ועד בית</h1>
                            <p className={styles.appSubtitle}>אנא מלא את הפרטים הבאים להרשמה</p>
                        </div>

                        <form onSubmit={handleSubmit} autoComplete="off">
                            <div className={styles.formGroup}>
                                <label htmlFor="fullName" className={styles.formLabel}>שם מלא</label>
                                <div className={styles.inputWrapper}>
                                    <input
                                        type="text"
                                        id="fullName"
                                        name="fullName"
                                        className={styles.formInput}
                                        placeholder="ישראל ישראלי"
                                        value={formData.fullName}
                                        onChange={handleChange}
                                        required
                                    />
                                    <div className={styles.inputIconWrapper}>
                                        <User className={styles.inputIcon} />
                                    </div>
                                </div>
                            </div>

                            <div className={styles.formGroup}>
                                <label htmlFor="email" className={styles.formLabel}>דואר אלקטרוני</label>
                                <div className={styles.inputWrapper}>
                                    <input
                                        type="email"
                                        id="email"
                                        name="email"
                                        className={styles.formInput}
                                        placeholder=""
                                        value={formData.email}
                                        onChange={handleChange}
                                        required
                                        autoComplete="off"
                                        autoCorrect="off"
                                        autoCapitalize="off"
                                        spellCheck="false"
                                    />
                                    <div className={styles.inputIconWrapper}>
                                        <Mail className={styles.inputIcon} />
                                    </div>
                                </div>
                            </div>

                            <div className={styles.formGroup}>
                                <label htmlFor="phone" className={styles.formLabel}>טלפון</label>
                                <div className={styles.inputWrapper}>
                                    <input
                                        type="tel"
                                        id="phone"
                                        name="phone"
                                        className={styles.formInput}
                                        placeholder="050-1234567"
                                        value={formData.phone}
                                        onChange={handleChange}
                                        required
                                    />
                                    <div className={styles.inputIconWrapper}>
                                        <Phone className={styles.inputIcon} />
                                    </div>
                                </div>
                                <small style={{ color: '#666', fontSize: '12px', display: 'block', marginTop: '4px', textAlign: 'right' }}>
                                    הקלד מספר טלפון - הפורמט יתווסף אוטומטית (050-1234567)
                                </small>
                            </div>

                            <div className={styles.formGroup}>
                                <label htmlFor="isvaad" className={styles.formLabel}>וועד?</label>
                                <div className={styles.inputWrapper}>
                                    <select
                                        id="isvaad"
                                        name="isvaad"
                                        className={styles.formInput}
                                        value={formData.isVaad}
                                        onChange={(e) => setFormData({ ...formData, isVaad: e.target.value })}
                                        required
                                    >
                                        <option value="">בחר אפשרות</option>
                                        <option value="כן">כן</option>
                                        <option value="לא">לא</option>
                                    </select>
                                </div>
                            </div>

                            <div className={styles.formRow}>
                                <div className={styles.formGroup}>
                                    <label htmlFor="city" className={styles.formLabel}>עיר</label>
                                    <div className={styles.inputWrapper}>
                                        <input
                                            type="text"
                                            id="city"
                                            name="city"
                                            className={styles.formInput}
                                            placeholder="הזן עיר"
                                            value={formData.city}
                                            onChange={handleChange}
                                            onFocus={() => {
                                                if (formData.city.trim() && !newCityMode) {
                                                    const filtered = israeliCities.filter(city =>
                                                        typeof city === 'string' &&
                                                        city.toLowerCase().includes(formData.city.toLowerCase())
                                                    );
                                                    setCitySuggestions(filtered);
                                                    setShowCitySuggestions(filtered?.length > 0);
                                                }
                                            }}
                                            required
                                        />
                                        <div className={styles.inputIconWrapper}>
                                            <Home className={styles.inputIcon} />
                                        </div>
                                    </div>

                                    {showCitySuggestions && !newCityMode && (
                                        <div
                                            ref={citySuggestionsRef}
                                            className={styles.suggestionsContainer}
                                        >
                                            <div className={styles.dropdownList}>
                                                {citySuggestions?.length > 0 ? (
                                                    citySuggestions?.map((city, index) => (
                                                        <div
                                                            key={index}
                                                            className={styles.dropdownItem}
                                                            onClick={() => handleSuggestionClick('city', city)}
                                                        >
                                                            {city}
                                                        </div>
                                                    ))
                                                ) : (
                                                    <div className={styles.dropdownEmpty}>לא נמצאו תוצאות</div>
                                                )}
                                            </div>

                                            <div className={styles.dropdownAction}>
                                                <div className={styles.actionButtons}>
                                                    <button
                                                        type="button"
                                                        className={styles.addNewButton}
                                                        onClick={() => handleAddNew('city')}
                                                    >
                                                        <span>
                                                            <Plus size={16} />
                                                            הוסף עיר חדשה
                                                        </span>
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className={styles.cancelButton}
                                                        onClick={() => setShowCitySuggestions(false)}
                                                    >
                                                        סגור
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className={styles.formGroup}>
                                <label htmlFor="address" className={styles.formLabel}>רחוב</label>
                                <div className={styles.inputWrapper}>
                                    <input
                                        type="text"
                                        id="address"
                                        name="address"
                                        className={styles.formInput}
                                        placeholder="הזן רחוב"
                                        value={formData.address}
                                        onChange={handleChange}
                                        onFocus={() => {
                                            if (formData.address.trim() && !newAddressMode) {
                                                debouncedSearchStreets(formData.address.trim());
                                            }
                                        }}
                                        required
                                    />
                                    <div className={styles.inputIconWrapper}>
                                        <Home className={styles.inputIcon} />
                                    </div>
                                </div>

                                {showAddressSuggestions && !newAddressMode && (
                                    <div
                                        ref={addressSuggestionsRef}
                                        className={styles.suggestionsContainer}
                                    >
                                        <div className={styles.dropdownList}>
                                            {isAddressSearching ? (
                                                <div className={styles.dropdownLoading}>
                                                    מחפש...
                                                </div>
                                            ) : addressSuggestions?.length > 0 ? (
                                                addressSuggestions?.map((street, index) => (
                                                    <div
                                                        key={index}
                                                        className={styles.dropdownItem}
                                                        onClick={() => handleSuggestionClick('address', street)}
                                                    >
                                                        {street}
                                                    </div>
                                                ))
                                            ) : (
                                                <div className={styles.dropdownEmpty}>לא נמצאו תוצאות</div>
                                            )}
                                        </div>

                                        <div className={styles.dropdownAction}>
                                            <div className={styles.actionButtons}>
                                                <button
                                                    type="button"
                                                    className={styles.addNewButton}
                                                    onClick={() => handleAddNew('address')}
                                                >
                                                    <span>
                                                        <Plus size={16} />
                                                        הוסף רחוב חדש
                                                    </span>
                                                </button>
                                                <button
                                                    type="button"
                                                    className={styles.cancelButton}
                                                    onClick={() => setShowAddressSuggestions(false)}
                                                >
                                                    סגור
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className={styles.formRow}>
                                <div className={styles.formGroup}>
                                    <label htmlFor="addressNumber" className={styles.formLabel}>מספר רחוב</label>
                                    <div className={styles.inputWrapper}>
                                        <input
                                            type="text"
                                            id="addressNumber"
                                            name="addressNumber"
                                            className={styles.formInput}
                                            placeholder="הזן מספר"
                                            value={formData.addressNumber}
                                            onChange={handleChange}
                                            required
                                        />
                                        <div className={styles.inputIconWrapper}>
                                            <Home className={styles.inputIcon} />
                                        </div>
                                    </div>
                                </div>
                                <div className={styles.formGroup}>
                                    <label htmlFor="numApartments" className={styles.formLabel}>מספר דירות</label>
                                    <div className={styles.inputWrapper}>
                                        <input
                                            type="text"
                                            id="numApartments"
                                            name="numApartments"
                                            className={styles.formInput}
                                            placeholder="הזן מספר"
                                            value={formData.numApartments}
                                            onChange={handleChange}
                                            required
                                        />
                                        <div className={styles.inputIconWrapper}>
                                            <Home className={styles.inputIcon} />
                                        </div>
                                    </div>
                                </div>
                                <div className={styles.formGroup}>
                                    <label htmlFor="numFloors" className={styles.formLabel}>מספר קומות (אופציונלי)</label>
                                    <div className={styles.inputWrapper}>
                                        <input
                                            type="text"
                                            id="numFloors"
                                            name="numFloors"
                                            className={styles.formInput}
                                            placeholder="הזן מספר"
                                            value={formData.numFloors}
                                            onChange={handleChange}
                                            autoComplete="off"
                                        />
                                        <div className={styles.inputIconWrapper}>
                                            <Home className={styles.inputIcon} />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className={styles.formGroup}>
                                <label htmlFor="password" className={styles.formLabel}>סיסמה</label>
                                <div className={styles.inputWrapper}>
                                    <input
                                        type={showPassword ? "text" : "password"}
                                        id="password"
                                        name="password"
                                        className={`${styles.formInput} ${passwordErrors.length > 0 ? styles.inputError : ''}`}
                                        placeholder=""
                                        value={formData.password}
                                        onChange={handleChange}
                                        required
                                        autoComplete="new-password"
                                        autoCorrect="off"
                                        autoCapitalize="off"
                                        spellCheck="false"
                                    />
                                    <div className={styles.inputIconWrapper}>
                                        <Lock className={styles.inputIcon} />
                                    </div>
                                    <button
                                        type="button"
                                        className={styles.passwordToggle}
                                        onClick={() => setShowPassword(!showPassword)}
                                    >
                                        {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                                    </button>
                                </div>
                                
                                {/* Password Requirements */}
                                <div className={styles.passwordRequirements}>
                                    <p className={styles.requirementsTitle}>דרישות הסיסמה:</p>
                                    <ul className={styles.requirementsList}>
                                        <li className={formData.password.length >= 8 ? styles.valid : styles.invalid}>
                                            לפחות 8 תווים
                                        </li>
                                        <li className={/[a-z]/.test(formData.password) ? styles.valid : styles.invalid}>
                                            אות קטנה באנגלית
                                        </li>
                                        <li className={/[0-9]/.test(formData.password) ? styles.valid : styles.invalid}>
                                            ספרה אחת לפחות
                                        </li>
                                    </ul>
                                </div>

                                {passwordErrors.length > 0 && (
                                    <div className={styles.passwordErrors}>
                                        {passwordErrors.map((error, index) => (
                                            <p key={index} className={styles.passwordError}>
                                                {error}
                                            </p>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <div className={styles.formGroup}>
                                <label htmlFor="confirmPassword" className={styles.formLabel}>אימות סיסמה</label>
                                <div className={styles.inputWrapper}>
                                    <input
                                        type={showConfirmPassword ? "text" : "password"}
                                        id="confirmPassword"
                                        name="confirmPassword"
                                        className={`${styles.formInput} ${formData.confirmPassword && formData.password !== formData.confirmPassword ? styles.inputError : ''}`}
                                        placeholder=""
                                        value={formData.confirmPassword}
                                        onChange={handleChange}
                                        required
                                        autoComplete="new-password"
                                        autoCorrect="off"
                                        autoCapitalize="off"
                                        spellCheck="false"
                                    />
                                    <div className={styles.inputIconWrapper}>
                                        <Lock className={styles.inputIcon} />
                                    </div>
                                    <button
                                        type="button"
                                        className={styles.passwordToggle}
                                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                    >
                                        {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                                    </button>
                                </div>
                                
                                {formData.confirmPassword && formData.password !== formData.confirmPassword && (
                                    <p className={styles.passwordError}>
                                        הסיסמאות אינן תואמות
                                    </p>
                                )}
                                
                                {formData.confirmPassword && formData.password === formData.confirmPassword && formData.confirmPassword.length > 0 && (
                                    <p className={styles.passwordMatch}>
                                        הסיסמאות תואמות ✓
                                    </p>
                                )}
                            </div>

                            <button
                                type="submit"
                                className={`${styles.registryButton} ${isLoading ? styles.disabled : ''}`}
                                disabled={isLoading}
                            >
                                {isLoading ? 'מעבד...' : 'הרשם'}
                            </button>
                        </form>
                    </div>

                    <div className={styles.loginContainer}>
                        <p className={styles.loginText}>
                            כבר יש לך חשבון? <a href="#" onClick={handleLoginClick} className={styles.link}>התחבר עכשיו</a>
                        </p>
                    </div>
                </div>

                <div className={styles.footer}>
                    <p className={styles.copyright}>© 2025 ניהול ועד בית. כל הזכויות שמורות.</p>
                </div>
            </div>
        </div>
    );
}